import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Play, X, BadgeCheck } from "lucide-react";
import { api } from "../lib/constants";
import { Reveal } from "../components/Reveal";
import { Overline, SectionTitle, GoldButton } from "../components/site/Primitives";
import SEO from "../components/SEO";

const FILTERS = ["Todos", "Arapiraca", "Garanhuns", "Maceió", "Afro", "Cacheado", "Liso", "Ondulado"];

function isBeforeAfter(item) {
  const t = (item.title || "").toLowerCase();
  return !item.video && (t.includes("antes e depois") || t.includes("prótese") || t.includes("protese"));
}

function matches(item, filter) {
  if (filter === "Todos") return true;
  const f = filter.toLowerCase().replace("ó", "o");
  return (item.city || "").toLowerCase() === f || (item.hair_type || "").toLowerCase() === f;
}

export default function Resultados() {
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState("Todos");
  const [videoSrc, setVideoSrc] = useState(null);

  useEffect(() => {
    api.get("/results").then(({ data }) => setResults(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => results.filter((r) => matches(r, filter)), [results, filter]);
  const hasDemo = filtered.some((r) => r.demo);

  return (
    <div>
      <SEO title="Resultados | Transformações Reais de Prótese Capilar" description="Galeria de transformações reais de prótese capilar em Arapiraca, Garanhuns e Maceió. Cabelo liso, ondulado, cacheado e afro." />

      <section className="border-b border-white/10 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><Overline className="mb-3">Galeria</Overline></Reveal>
          <Reveal delay={0.05}><SectionTitle>Transformações reais</SectionTitle></Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed">
              Cada cliente possui um rosto, um estilo e uma história diferente. Por isso, cada transformação é personalizada.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12 lg:py-16">
        <div className="flex flex-wrap gap-2.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`result-filter-${f.toLowerCase()}`}
              className={`font-heading font-500 text-sm tracking-wide px-5 py-2.5 rounded-sm border transition-colors ${
                filter === f ? "bg-gold text-black border-gold" : "border-white/15 text-zinc-300 hover:border-white/40"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-10">
          {filtered.map((r, i) => (
            <Reveal key={r.id} delay={(i % 4) * 0.05}>
              <div
                onClick={() => r.video && setVideoSrc(r.video)}
                data-testid={r.video ? `result-video-${r.id}` : `result-${r.id}`}
                className={`group relative overflow-hidden rounded-sm border border-white/10 aspect-[3/4] ${r.fit === "contain" ? "bg-[#0a0a0a]" : ""} ${r.video ? "cursor-pointer hover:border-gold/60" : ""}`}
              >
                <img src={r.image} alt={r.title} className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${r.fit === "contain" ? "object-contain" : "object-cover"}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent pointer-events-none" />
                {isBeforeAfter(r) && (
                  <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm border border-gold/50 text-gold rounded-full pl-2 pr-2.5 py-1 shadow-lg pointer-events-none" data-testid={`result-badge-${r.id}`}>
                    <BadgeCheck className="w-3.5 h-3.5" />
                    <span className="font-heading font-600 uppercase tracking-wide text-[10px]">Resultado real</span>
                  </div>
                )}
                {r.video && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-14 h-14 rounded-full bg-gold/90 text-black flex items-center justify-center shadow-xl transition-transform group-hover:scale-110">
                      <Play className="w-6 h-6 ml-0.5 fill-black" />
                    </span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="font-heading font-600 text-white text-sm">{r.title}</p>
                  <span className="overline-label text-[10px] text-gold capitalize">{[r.city, r.hair_type, r.video ? "Vídeo" : null].filter(Boolean).join(" · ")}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && <p className="text-zinc-500 mt-10">Nenhum resultado para este filtro ainda.</p>}
        {hasDemo && <p className="text-xs text-zinc-600 mt-8">Imagens ilustrativas — substitua pelos seus resultados reais no painel administrativo.</p>}

        <div className="mt-14 text-center">
          <GoldButton to="/protese-capilar" size="lg" data-testid="resultados-cta"><ArrowRight className="w-4 h-4" /> QUERO TER MEU RESULTADO</GoldButton>
        </div>
      </section>

      {videoSrc && (
        <div className="fixed inset-0 z-[70] bg-black/92 flex items-center justify-center p-4" onClick={() => setVideoSrc(null)} data-testid="video-lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white z-10" onClick={() => setVideoSrc(null)} aria-label="Fechar"><X className="w-8 h-8" /></button>
          <video src={videoSrc} controls autoPlay playsInline className="max-h-[88vh] max-w-full rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
